import { FC, useEffect, useMemo, useState } from 'react';
import { createSaleEstimate, fetchOrder, type OrderResponse } from '../../../api/orders';

interface OrderPaymentPosProps {
  orderId: string | null;
}

const OrderPaymentPos: FC<OrderPaymentPosProps> = ({ orderId }) => {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [responsePreview, setResponsePreview] = useState<string | null>(null);
  const [infoMessage, setInfoMessage] = useState<string | null>(null);
  const [order, setOrder] = useState<OrderResponse | null>(null);
  const [loadingOrder, setLoadingOrder] = useState(false);

  const loadOrder = async (id: string) => {
    setLoadingOrder(true);
    try {
      const resp = await fetchOrder(id);
      setOrder(resp);
      if (resp.status === 'delivered') {
        setInfoMessage('Payment completed and vehicle delivered.');
      } else if ((resp as any).saleEstimateErpId && (resp as any).saleEstimateErpId > 0) {
        setInfoMessage('Sale sent to POS. Awaiting payment confirmation.');
      } else {
        setInfoMessage(null);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load order.');
    } finally {
      setLoadingOrder(false);
    }
  };

  useEffect(() => {
    if (!orderId) {
      setOrder(null);
      return;
    }
    loadOrder(orderId);
  }, [orderId]);

  const payments = order?.paymentSummaries ?? [];
  const paymentTotal = useMemo(
    () => payments.reduce((acc, p) => acc + Number(p.amount ?? 0), 0),
    [payments],
  );
  const lastPaymentAt = useMemo(() => {
    const dates = payments
      .map((p) => (p.happenedAt ? new Date(p.happenedAt) : null))
      .filter((d): d is Date => !!d && !Number.isNaN(d.getTime()));
    return dates.length ? new Date(Math.max(...dates.map((d) => d.getTime()))) : null;
  }, [payments]);

  const formatAmount = (value: string | number | null | undefined) => {
    const numeric = typeof value === 'number' ? value : Number(value ?? 0);
    if (Number.isNaN(numeric)) return '0.00';
    return numeric.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };
  const formatDateTime = (value: string | Date | null | undefined) => {
    if (!value) return '—';
    try {
      const d = value instanceof Date ? value : new Date(value);
      return d.toLocaleString();
    } catch {
      return String(value);
    }
  };

  const handleSendSaleEstimate = async () => {
    if (!orderId) {
      setError('Order id is missing. Save the order before sending to POS.');
      return;
    }

    setSubmitting(true);
    setError(null);
    setResponsePreview(null);

    try {
      const response = await createSaleEstimate(orderId) as any;
      // On success, only show the friendly message; do not render raw response
      setResponsePreview(null);
      const msg = (response && typeof response === 'object' && 'message' in response)
        ? String((response as any).message)
        : 'Sale sent to POS. Awaiting payment confirmation.';
      setInfoMessage(msg);
      // Refresh order so saleEstimateErpId and totals/items reflect latest state without manual page reload
      await loadOrder(orderId);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to send sale estimate.';
      setError(message);
      // Show error details block for troubleshooting
      setResponsePreview(typeof err === 'object' ? JSON.stringify({ error: message }, null, 2) : String(message));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-4">
      {order?.status === 'delivered' ? (
        <p className="text-sm text-slate-600">
          This order has been delivered. Payment has been completed and the payment details are shown below.
        </p>
      ) : (
        <p className="text-sm text-slate-600">
          Send the order to POS for completing the payment. The customer and vehicle details are pulled automatically from this order.
        </p>
      )}

      {(order as any)?.saleEstimateErpId || order?.status === 'done' || order?.status === 'delivered' ? null : (
        <button
          type="button"
          className="inline-flex items-center justify-center rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white shadow hover:bg-primary/90 disabled:opacity-60"
          onClick={handleSendSaleEstimate}
          disabled={submitting || loadingOrder}
        >
          {submitting ? 'Sending…' : 'Trigger Payment'}
        </button>
      )}
      {error ? (
        <div className="rounded-lg border border-danger/30 bg-danger/10 px-4 py-3 text-sm text-danger">{error}</div>
      ) : null}
      {infoMessage ? (
        <div className="rounded-lg border border-emerald-300/40 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">{infoMessage}</div>
      ) : null}

      {/* Show order items and financial summary similar to manager/customer views */}
      {order ? (
        <div className="grid gap-4 lg:grid-cols-[2fr_minmax(260px,1fr)]">
          {order.items && order.items.length > 0 ? (
            <div className="rounded-2xl border border-slate-200 bg-white p-4 text-sm text-slate-600">
              <h3 className="text-base font-semibold text-slate-700">Order items</h3>
              <div className="mt-4 overflow-x-auto">
                <table className="min-w-full table-fixed border-separate border-spacing-y-2">
                  <thead>
                    <tr className="text-left text-xs uppercase tracking-wide text-slate-400">
                      <th className="px-3 py-2 font-semibold">Item</th>
                      <th className="px-3 py-2 font-semibold w-20 text-right">Qty</th>
                      <th className="px-3 py-2 font-semibold w-28 text-right">Unit</th>
                      <th className="px-3 py-2 font-semibold w-28 text-right">Subtotal</th>
                      <th className="px-3 py-2 font-semibold w-28 text-right">Discount</th>
                      <th className="px-3 py-2 font-semibold w-28 text-right">Tax</th>
                      <th className="px-3 py-2 font-semibold w-28 text-right">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {order.items
                      .slice()
                      .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
                      .map((item) => {
                        const name = item.productVariant?.name || item.description || '—';
                        return (
                          <tr key={item.id} className="bg-slate-50/60">
                            <td className="px-3 py-2 align-top">
                              <div className="text-slate-800">{name}</div>
                              {item.itemType ? (
                                <div className="mt-0.5 text-xs text-slate-400">{item.itemType}</div>
                              ) : null}
                            </td>
                            <td className="px-3 py-2 text-right align-top">{item.quantity ?? '—'}</td>
                            <td className="px-3 py-2 text-right align-top">{item.unitPrice ?? '—'}</td>
                            <td className="px-3 py-2 text-right align-top">{item.lineSubtotal ?? '—'}</td>
                            <td className="px-3 py-2 text-right align-top">{item.discountTotal ?? '—'}</td>
                            <td className="px-3 py-2 text-right align-top">{item.taxTotal ?? '—'}</td>
                            <td className="px-3 py-2 text-right align-top font-semibold text-slate-800">
                              {item.lineTotal ?? '—'}
                            </td>
                          </tr>
                        );
                      })}
                  </tbody>
                </table>
              </div>
            </div>
          ) : null}

          <div className="space-y-4">
            <div className="rounded-2xl border border-slate-200 bg-slate-50/60 p-4 text-sm text-slate-600">
              <h3 className="text-base font-semibold text-slate-700">Amount summary</h3>
              <dl className="mt-3 space-y-2 text-sm">
                <div className="flex items-center justify-between">
                  <dt className="text-xs font-semibold uppercase tracking-wide text-slate-400">Subtotal</dt>
                  <dd className="text-slate-700">{formatAmount(order.subtotalAmount ?? 0)}</dd>
                </div>
                <div className="flex items-center justify-between">
                  <dt className="text-xs font-semibold uppercase tracking-wide text-slate-400">Items discount</dt>
                  <dd className="text-slate-700">{formatAmount(order.discountTotal ?? 0)}</dd>
                </div>
                <div className="flex items-center justify-between">
                  <dt className="text-xs font-semibold uppercase tracking-wide text-slate-400">Tax</dt>
                  <dd className="text-slate-700">{formatAmount(order.taxTotal ?? 0)}</dd>
                </div>
                <div className="flex items-center justify-between">
                  <dt className="text-xs font-semibold uppercase tracking-wide text-slate-400">Grand total</dt>
                  <dd className="text-base font-semibold text-slate-800">
                    {formatAmount(order.grandTotal ?? 0)}
                  </dd>
                </div>
                {(order as any).saleEstimateErpId ? (
                  <div className="flex items-center justify-between">
                    <dt className="text-xs font-semibold uppercase tracking-wide text-slate-400">
                      Sale estimate ERP id
                    </dt>
                    <dd className="text-slate-700">{(order as any).saleEstimateErpId}</dd>
                  </div>
                ) : null}
              </dl>
            </div>
          </div>
        </div>
      ) : null}

      {order && (order.status === 'done' || order.status === 'delivered') ? (
        <div className="rounded-2xl border border-slate-200 bg-white p-4">
          <h3 className="text-base font-semibold text-slate-700">Payments</h3>
          {payments.length === 0 ? (
            <p className="mt-2 text-sm text-slate-500">No payment records found.</p>
          ) : (
            <>
              <ul className="mt-3 divide-y divide-slate-200 rounded-xl border border-slate-200 bg-white">
                {payments.map((p) => (
                  <li key={p.id} className="flex items-center justify-between px-3 py-2">
                    <div>
                      <div className="text-slate-700">{p.paymentTypeName}</div>
                      <div className="text-xs text-slate-400">{formatDateTime(p.happenedAt)}</div>
                    </div>
                    <div className="text-sm font-semibold text-slate-800">{formatAmount(p.amount)}</div>
                  </li>
                ))}
              </ul>
              <div className="mt-3 flex items-center justify-between text-sm">
                <div className="text-slate-500">Last payment at</div>
                <div className="font-semibold text-slate-700">{formatDateTime(lastPaymentAt)}</div>
              </div>
              <div className="mt-1 flex items-center justify-between text-sm">
                <div className="text-slate-500">Total paid</div>
                <div className="font-semibold text-slate-700">{formatAmount(paymentTotal)}</div>
              </div>
            </>
          )}
          <div className="mt-4">
            <button
              type="button"
              className="inline-flex items-center justify-center rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-600 transition hover:border-primary hover:text-primary disabled:opacity-60"
              onClick={() => orderId && loadOrder(orderId)}
              disabled={loadingOrder}
            >
              {loadingOrder ? 'Refreshing…' : 'Refresh payment details'}
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default OrderPaymentPos;
